// The firm's money, for real: the office master file aggregated (dollars,
// labeled by its sync date) and the online invoice/payment ledger (cents).
// The two never mix arithmetically. Empty is empty; nothing is invented.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  downloadCollectedCsv,
  reverseManualPayment,
  type CollectedMonth,
  type InvoiceSortKey,
  type LedgerSort,
  type PaymentSortKey,
} from "../api/billing";
import { CollectedBars } from "../components/CollectedBars";
import { NewInvoiceDialog } from "../components/NewInvoiceDialog";
import { useCurrentUser } from "../../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../../lib/roles";
import { useModuleOn } from "../../../api/attorneyConfig";
import { ModuleOffNotice } from "../../../components/chrome/ModuleOffNotice";
import { AdminOnlyCard } from "../../../components/chrome/AdminOnlyCard";
import { Skeleton, SkeletonGroup } from "../../../components/chrome/Skeleton";
import { SEP } from "../../../components/chrome/SepList";
import {
  useBillingInvoices,
  useBillingPayments,
  useBillingPlans,
  useBillingSummary,
  useCollectedDailyReport,
  useCollectedReport,
} from "../hooks/useBilling";
import {
  enumerateDays,
  enumerateMonths,
  isDayString,
  lastDaysRange,
  previousRange,
  spanDays,
  thisQuarterRange,
  thisYearRange,
  zeroFill,
} from "../lib/reportRanges";
import { fmtCents, fmtDollars } from "../../../lib/money";
import { INVOICE_PILL, METHOD_LABEL, PAYMENT_LABEL, PAYMENT_PILL, day } from "../lib/billingLabels";

// Every track but the description is fixed and the heading row shares the
// class: each row is its own grid, so an auto track sized by that row's
// content would put the same column at a different x. A class, not an
// inline style, so a phone can fold the rows.
const INVOICE_COLS = "cc-money-cols-invoices";
const PAYMENT_COLS = "cc-money-cols-payments";
const PLAN_COLS = "cc-money-cols-plans";

const LEDGERS = [
  { key: "invoices" as const, label: "Invoices" },
  { key: "payments" as const, label: "Payments" },
  { key: "plans" as const, label: "Payment plans" },
  { key: "reports" as const, label: "Reports" },
];

// The report keeps its columns on a phone: the class widens them and keeps
// the rows out of the fold.
const REPORT_COLS = "cc-money-cols-report";

function monthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return `${new Date(y, m - 1, 1).toLocaleString("en-US", { month: "short" })} ${y}`;
}

function dayLabel(dayStr: string): string {
  const [y, m, d] = dayStr.split("-").map(Number);
  return `${new Date(y, m - 1, d).toLocaleString("en-US", { month: "short" })} ${d}`;
}

type PresetKey = "7d" | "30d" | "quarter" | "year" | "custom";

const PRESETS: Array<{
  key: Exclude<PresetKey, "custom">;
  label: string;
  range: () => { from: string; to: string };
}> = [
  { key: "7d", label: "Last 7 days", range: () => lastDaysRange(7) },
  { key: "30d", label: "Last 30 days", range: () => lastDaysRange(30) },
  { key: "quarter", label: "This quarter", range: () => thisQuarterRange() },
  { key: "year", label: "This year", range: () => thisYearRange() },
];

type ReportRow = Omit<CollectedMonth, "month"> & { period: string };

// The prior period can be zero or negative money, where a percentage is
// nonsense - say what actually happened instead.
function deltaText(cur: number, prevCents: number): string | null {
  if (prevCents === 0 && cur === 0) return null;
  if (prevCents === 0) return "no money in the prior period";
  if (prevCents < 0) return `prior period ${fmtCents(prevCents)}`;
  const pct = Math.round(((cur - prevCents) / prevCents) * 100);
  return `${pct >= 0 ? "+" : ""}${pct}% vs prior period`;
}

function ReportTile(props: { label: string; cents: number; prevCents: number | null }) {
  const delta = props.prevCents === null ? null : deltaText(props.cents, props.prevCents);
  const dir = props.prevCents !== null && props.cents >= props.prevCents ? "up" : "down";
  return (
    <div className="cc-billing-tile">
      <div className="label">{props.label}</div>
      <div className="value">{fmtCents(props.cents)}</div>
      {delta && <div className={`sub delta ${dir}`}>{delta}</div>}
    </div>
  );
}

const ARROW_UP = String.fromCharCode(8593);
const ARROW_DOWN = String.fromCharCode(8595);

// Same key toggles direction; a new key starts newest/largest-first.
function toggled<K extends string>(cur: LedgerSort<K>, key: K): LedgerSort<K> {
  return cur.key === key ? { key, dir: cur.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" };
}

function SortHead<K extends string>(props: {
  label: string;
  sortKey: K;
  sort: LedgerSort<K>;
  onSort: (key: K) => void;
}) {
  const active = props.sort.key === props.sortKey;
  return (
    <button type="button" className="cc-sort-btn" onClick={() => props.onSort(props.sortKey)}>
      {props.label}
      {active ? ` ${props.sort.dir === "desc" ? ARROW_DOWN : ARROW_UP}` : ""}
    </button>
  );
}

export default function BillingIndex() {
  const user = useCurrentUser();
  const isAdmin = isAdminRole(user?.role);
  const billingOn = useModuleOn("billing");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [ledger, setLedger] = useState<(typeof LEDGERS)[number]["key"]>("invoices");
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [reverseError, setReverseError] = useState<string | null>(null);
  const [reverseNote, setReverseNote] = useState<string | null>(null);
  const reverseMut = useMutation({
    mutationFn: (paymentId: string) => reverseManualPayment(paymentId),
    onSuccess: async (_res, paymentId) => {
      const row = payments.find((p) => p._id === paymentId);
      setReverseError(null);
      setReverseNote(
        row
          ? `Reversed ${fmtCents(row.amountCents)}${row.invoiceNumber ? ` on ${row.invoiceNumber}` : ""} - that bill is open again.`
          : "The payment was reversed and its bill is open again.",
      );
      await queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
    onError: (err) => {
      setReverseNote(null);
      setReverseError(err instanceof Error ? err.message : "Could not reverse it.");
    },
  });
  const [invSort, setInvSort] = useState<LedgerSort<InvoiceSortKey>>({
    key: "createdAt",
    dir: "desc",
  });
  const [paySort, setPaySort] = useState<LedgerSort<PaymentSortKey>>({
    key: "date",
    dir: "desc",
  });
  const [period, setPeriod] = useState(() => thisYearRange());
  const [preset, setPreset] = useState<PresetKey>("year");
  const [exportError, setExportError] = useState<string | null>(null);
  const summaryQ = useBillingSummary(isAdmin);
  const invoicesQ = useBillingInvoices(invSort, isAdmin);
  const paymentsQ = useBillingPayments(paySort, isAdmin);
  const plansQ = useBillingPlans(isAdmin);
  // A date input mid-edit hands over "" - nothing may fetch, count or
  // compare until both ends are whole days again.
  const validRange = isDayString(period.from) && isDayString(period.to);
  // Bars want day buckets, but a year of day bars is unreadable - long
  // ranges stay monthly. A reversed custom range goes monthly and lets
  // the server's 400 surface as the report error.
  const span = spanDays(period.from, period.to);
  const dayMode = span >= 1 && span <= 92;
  const prev = previousRange(period.from, period.to);
  const onReports = ledger === "reports" && validRange;
  const reportQ = useCollectedReport(period.from, period.to, onReports && !dayMode);
  const dailyQ = useCollectedDailyReport(period.from, period.to, onReports && dayMode);
  const prevMonthlyQ = useCollectedReport(prev.from, prev.to, onReports && !dayMode);
  const prevDailyQ = useCollectedDailyReport(prev.from, prev.to, onReports && dayMode);
  const curQ = dayMode ? dailyQ : reportQ;
  const prevQ = dayMode ? prevDailyQ : prevMonthlyQ;

  if (!isAdmin) return <AdminOnlyCard user={user} what="read the firm's billing" />;

  if (summaryQ.isLoading) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <SkeletonGroup label="Loading billing">
            <Skeleton width="30%" height={22} />
            <Skeleton width="100%" height={84} radius={10} />
            <Skeleton width="100%" height={84} radius={10} />
          </SkeletonGroup>
        </div>
      </main>
    );
  }

  const summary = summaryQ.data?.summary ?? null;
  const invoices = invoicesQ.data?.invoices ?? [];
  const payments = paymentsQ.data?.payments ?? [];
  const received = payments.filter((p) => p.status === "succeeded").length;
  const notReceived = payments.length - received;
  const plans = plansQ.data?.plans ?? [];
  // Payment rows are attempts, not money, so the breakdown says more than
  // a count - but only a complete list can be broken down, and a capped
  // page falls back to the true total.
  const ledgerSub =
    ledger === "invoices"
      ? invoicesQ.data
        ? `${invoicesQ.data.total} total`
        : "Bills the firm issued"
      : ledger === "plans"
        ? "Active installment schedules"
        : ledger === "reports"
          ? `Cash basis ${SEP} ${period.from} to ${period.to}`
          : !paymentsQ.data
            ? "Online and recorded by the office"
            : payments.length === paymentsQ.data.total
              ? `${received} received${notReceived > 0 ? ` ${SEP} ${notReceived} not completed` : ""}`
              : `${paymentsQ.data.total} total`;
  const anyError = summaryQ.isError || invoicesQ.isError || paymentsQ.isError || plansQ.isError;

  const reportRows: ReportRow[] = dayMode
    ? (dailyQ.data?.days ?? []).map(({ day: d, ...r }) => ({ period: d, ...r }))
    : (reportQ.data?.months ?? []).map(({ month, ...r }) => ({ period: month, ...r }));
  const periodLabel = dayMode ? dayLabel : monthLabel;
  // The server omits quiet buckets; the bars still show their slots. A
  // typo year ("0202") is a valid day string spanning centuries - past
  // 92 buckets the chart bows out and the table carries the period.
  const bucketKeys = dayMode
    ? enumerateDays(period.from, period.to)
    : enumerateMonths(period.from, period.to);
  const chartRows =
    bucketKeys.length >= 1 && bucketKeys.length <= 92
      ? zeroFill(bucketKeys, new Map(reportRows.map((r) => [r.period, r]))).map((r) => ({
          ...r,
          label: periodLabel(r.bucket),
        }))
      : null;
  const totals = curQ.data?.totals ?? null;
  const prevTotals = prevQ.data?.totals ?? null;

  return (
    <main className="cc-main">
      <div className="cc-body">
        <ModuleOffNotice id="billing" name="Billing" />
        <div className="cc-index-head">
          <span className="title">Billing</span>
          <span className="blurb">
            The office master file and online payments, side by side - each keeps its own totals,
            never added together.
          </span>
          {billingOn && (
            <button type="button" className="cc-btn-primary" onClick={() => setInvoiceOpen(true)}>
              New invoice
            </button>
          )}
        </div>

        <NewInvoiceDialog open={billingOn && invoiceOpen} onClose={() => setInvoiceOpen(false)} />

        {anyError && (
          <div
            className="cc-card"
            style={{ padding: "10px 18px", marginBottom: 12, color: "var(--warn-600)" }}
          >
            Could not reach the server - some billing data is unavailable.
          </div>
        )}

        {summary ? (
          <div className="cc-billing-tiles" aria-label="Master file totals">
            <div className="cc-billing-tile">
              <span className="label">Fees on file</span>
              <span className="value">{fmtDollars(summary.totalDollars ?? 0)}</span>
              <span className="sub">from the office master file</span>
            </div>
            <div className="cc-billing-tile">
              <span className="label">Collected</span>
              <span className="value">{fmtDollars(summary.paidDollars ?? 0)}</span>
              <span className="sub">as recorded in the spreadsheet</span>
            </div>
            <div className="cc-billing-tile">
              <span className="label">Outstanding</span>
              <span className="value">{fmtDollars(summary.balanceDollars ?? 0)}</span>
              <span className="sub">as recorded in the spreadsheet</span>
            </div>
          </div>
        ) : (
          !summaryQ.isError && (
            <div className="cc-card" style={{ padding: "10px 18px", color: "var(--muted)" }}>
              No financial columns have come through the Excel sync yet.
            </div>
          )
        )}
        {/* Each tile sums only the rows that carry its column, so the three
            need not reconcile - say what the count really counts. Outside
            the grid: a full-width cell there holds every auto-fit track
            open and squeezes the tiles into a third of the page. */}
        {summary && (
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--muted)" }}>
            {summary.casesWithFinancials} case{summary.casesWithFinancials === 1 ? "" : "s"} carry
            at least one money column
            {summary.syncedAt ? `, synced ${day(summary.syncedAt)}` : ""}.
          </div>
        )}

        <div className="cc-card" style={{ marginTop: 14 }}>
          <div className="cc-card-head">
            <div className="cc-tabs" role="tablist" aria-label="Billing ledgers">
              {LEDGERS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={ledger === t.key}
                  className={ledger === t.key ? "active" : undefined}
                  onClick={() => setLedger(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="actions">
              <span className="cc-card-sub">{ledgerSub}</span>
            </div>
          </div>

          {ledger === "invoices" && (
            <div role="tabpanel">
              {!invoicesQ.isLoading && !invoicesQ.isError && invoices.length === 0 && (
                <div style={{ padding: "8px 18px", color: "var(--muted)", fontSize: 13 }}>
                  No invoices issued yet.
                  {billingOn && " Issue one here, or from the matter's own Billing card."}
                </div>
              )}
              {invoices.length > 0 && (
                <>
                  <div className={`cc-money-head ${INVOICE_COLS}`}>
                    <span className="amt">
                      <SortHead
                        label="Amount"
                        sortKey="amountCents"
                        sort={invSort}
                        onSort={(k) => setInvSort(toggled(invSort, k))}
                      />
                    </span>
                    <span>Invoice, client and service</span>
                    <span>
                      <SortHead
                        label="Issued"
                        sortKey="createdAt"
                        sort={invSort}
                        onSort={(k) => setInvSort(toggled(invSort, k))}
                      />
                    </span>
                    <span>
                      <SortHead
                        label="Status"
                        sortKey="status"
                        sort={invSort}
                        onSort={(k) => setInvSort(toggled(invSort, k))}
                      />
                    </span>
                  </div>
                  {/* The whole row navigates; the amount cell keeps the one
                      real link so keyboard and screen readers still get a
                      named target per row. */}
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {invoices.map((inv) => (
                      <li
                        key={inv._id}
                        className={`cc-money-row cc-row-lift ${INVOICE_COLS}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/billing/invoice/${inv._id}`)}
                      >
                        <span className="amt">
                          <Link
                            className="cc-link-subtle"
                            to={`/billing/invoice/${inv._id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {fmtCents(inv.amountCents)}
                          </Link>
                        </span>
                        <span style={{ fontSize: 13 }}>
                          <span className="tnum-mono">{inv.number ?? ""}</span>
                          {inv.number ? " " : ""}
                          {inv.clientLabel ?? "Unknown client"}
                          {SEP}
                          {inv.description}
                        </span>
                        <span className="tnum" style={{ fontSize: 12, color: "var(--muted)" }}>
                          {day(inv.createdAt)}
                        </span>
                        <span className={`cc-pill ${INVOICE_PILL[inv.status] ?? "muted"}`}>
                          {inv.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {invoicesQ.data && invoicesQ.data.total > invoices.length && (
                <div style={{ padding: "8px 18px", fontSize: 12, color: "var(--muted)" }}>
                  Showing {invoices.length} of {invoicesQ.data.total}.
                </div>
              )}
            </div>
          )}

          {ledger === "payments" && (
            <div role="tabpanel">
              {reverseError && (
                <div role="alert" className="cc-card-note bad">
                  {reverseError}
                </div>
              )}
              {reverseNote && <div className="cc-card-note">{reverseNote}</div>}
              {!paymentsQ.isLoading && !paymentsQ.isError && payments.length === 0 && (
                <div style={{ padding: "8px 18px", color: "var(--muted)", fontSize: 13 }}>
                  No payments yet.
                  {summaryQ.data?.configured
                    ? " Online payments appear the moment a client pays an invoice; checks and cash the office takes are recorded here too."
                    : " Online payment is not configured, so only payments the office records by hand will appear here."}
                </div>
              )}
              {payments.length > 0 && (
                <>
                  <div className={`cc-money-head ${PAYMENT_COLS}`}>
                    <span className="amt">
                      <SortHead
                        label="Amount"
                        sortKey="amountCents"
                        sort={paySort}
                        onSort={(k) => setPaySort(toggled(paySort, k))}
                      />
                    </span>
                    <span>Client and invoice</span>
                    <span>
                      <SortHead
                        label="Date"
                        sortKey="date"
                        sort={paySort}
                        onSort={(k) => setPaySort(toggled(paySort, k))}
                      />
                    </span>
                    <span>How it arrived</span>
                    <span>
                      <SortHead
                        label="Status"
                        sortKey="status"
                        sort={paySort}
                        onSort={(k) => setPaySort(toggled(paySort, k))}
                      />
                    </span>
                    <span />
                  </div>
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {payments.map((p) => (
                      <li
                        key={p._id}
                        className={`cc-money-row cc-row-lift ${PAYMENT_COLS}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/billing/payment/${p._id}`)}
                      >
                        <span className="amt">
                          <Link
                            className="cc-link-subtle"
                            to={`/billing/payment/${p._id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {fmtCents(p.amountCents)}
                          </Link>
                        </span>
                        <span style={{ fontSize: 13 }}>
                          {p.clientLabel ?? "Unknown client"}
                          {p.invoiceNumber ? (
                            <>
                              {SEP}
                              <span className="tnum-mono">{p.invoiceNumber}</span>
                            </>
                          ) : null}
                        </span>
                        <span className="tnum" style={{ fontSize: 12, color: "var(--muted)" }}>
                          {day(p.paidAt ?? p.createdAt)}
                        </span>
                        {/* How the money arrived is one fact, not a state - only
                      the pill beside it carries state. The reference rides
                      in the same cell: on its own track it left a hole in
                      every row that has no check number. An online payment's
                      reference is the number Stripe printed on its invoice,
                      which carries ours on its face (d14-A). */}
                        <span style={{ fontSize: 12, color: "var(--muted)" }}>
                          {p.provider === "manual"
                            ? METHOD_LABEL[p.method ?? "other"]
                            : `Online${p.livemode === false ? " (test)" : ""}`}
                          {p.provider === "manual" && p.reference ? (
                            <span className="tnum-mono"> #{p.reference}</span>
                          ) : null}
                          {p.provider === "stripe" && p.providerInvoiceNumber ? (
                            <span className="tnum-mono"> {p.providerInvoiceNumber}</span>
                          ) : null}
                        </span>
                        <span style={{ display: "flex", gap: 8 }}>
                          {p.disputeStatus === "open" && (
                            <span className="cc-pill crit">Disputed</span>
                          )}
                          <span className={`cc-pill ${PAYMENT_PILL[p.status] ?? "muted"}`}>
                            {PAYMENT_LABEL[p.status] ?? p.status}
                          </span>
                        </span>
                        {/* The actions cell acts, it never navigates - a
                            declined Reverse confirm must not bubble into
                            the row's click. */}
                        <span className="actions" onClick={(e) => e.stopPropagation()}>
                          {p.receiptUrl ? (
                            <a
                              className="cc-link-btn"
                              href={p.receiptUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Receipt
                            </a>
                          ) : null}
                          {p.provider === "manual" && p.status === "succeeded" && billingOn && (
                            <button
                              type="button"
                              className="cc-link-btn danger"
                              disabled={reverseMut.isPending}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Reverse the ${fmtCents(p.amountCents)} ${(
                                      METHOD_LABEL[p.method ?? "other"] ?? "offline"
                                    ).toLowerCase()} payment from ${p.clientLabel ?? "this client"}${
                                      p.invoiceNumber ? ` on ${p.invoiceNumber}` : ""
                                    }? The bill goes back to open and the client sees it as due again.`,
                                  )
                                ) {
                                  reverseMut.mutate(p._id);
                                }
                              }}
                            >
                              Reverse
                            </button>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {paymentsQ.data && paymentsQ.data.total > payments.length && (
                <div style={{ padding: "8px 18px", fontSize: 12, color: "var(--muted)" }}>
                  Showing {payments.length} of {paymentsQ.data.total}.
                </div>
              )}
            </div>
          )}

          {ledger === "plans" && (
            <div role="tabpanel">
              {!plansQ.isLoading && !plansQ.isError && plans.length === 0 && (
                <div style={{ padding: "8px 18px", color: "var(--muted)", fontSize: 13 }}>
                  No active payment plans.
                  {billingOn && " Staff start them from a matter's Billing card."}
                </div>
              )}
              {plans.length > 0 && (
                <>
                  <div className={`cc-money-head ${PLAN_COLS}`}>
                    <span className="amt">Total</span>
                    <span>Client</span>
                    <span>Progress</span>
                  </div>
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {plans.map((p) => (
                      <li key={p._id} className={`cc-money-row ${PLAN_COLS}`}>
                        <span className="amt">{fmtCents(p.totalCents)}</span>
                        <span style={{ fontSize: 13 }}>{p.clientLabel ?? "Unknown client"}</span>
                        <span className="tnum" style={{ fontSize: 12, color: "var(--muted)" }}>
                          {p.paidCount} of {p.installmentCount} paid
                          {p.nextDueDate && p.nextAmountCents != null
                            ? ` ${SEP} next ${fmtCents(p.nextAmountCents)} due ${day(p.nextDueDate)}`
                            : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {ledger === "reports" && (
            <div role="tabpanel">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                  padding: "10px 18px",
                }}
              >
                {PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className="cc-link-btn"
                    aria-pressed={preset === p.key}
                    onClick={() => {
                      setPreset(p.key);
                      setPeriod(p.range());
                    }}
                  >
                    {p.label}
                  </button>
                ))}
                <label style={{ fontSize: 12, color: "var(--muted)" }}>
                  From{" "}
                  <input
                    type="date"
                    className="cc-field"
                    value={period.from}
                    onChange={(e) => {
                      setPreset("custom");
                      setPeriod({ ...period, from: e.target.value });
                    }}
                  />
                </label>
                <label style={{ fontSize: 12, color: "var(--muted)" }}>
                  To{" "}
                  <input
                    type="date"
                    className="cc-field"
                    value={period.to}
                    onChange={(e) => {
                      setPreset("custom");
                      setPeriod({ ...period, to: e.target.value });
                    }}
                  />
                </label>
                <button
                  type="button"
                  className="cc-link-btn"
                  style={{ marginLeft: "auto" }}
                  disabled={!curQ.data}
                  onClick={() => {
                    setExportError(null);
                    downloadCollectedCsv(period.from, period.to).catch((err) =>
                      setExportError(
                        err instanceof Error ? err.message : "Could not download the CSV.",
                      ),
                    );
                  }}
                >
                  Export CSV
                </button>
              </div>
              {exportError && (
                <div style={{ padding: "0 18px 8px", color: "var(--warn-600)", fontSize: 13 }}>
                  {exportError}
                </div>
              )}
              {!validRange && (
                <div style={{ padding: "8px 18px", color: "var(--muted)", fontSize: 13 }}>
                  Pick a complete From and To date to run the report.
                </div>
              )}
              {curQ.isLoading && (
                <div style={{ padding: "8px 18px 14px" }}>
                  <SkeletonGroup label="Loading report">
                    <Skeleton width="100%" height={40} radius={8} />
                    <Skeleton width="100%" height={40} radius={8} />
                  </SkeletonGroup>
                </div>
              )}
              {curQ.isError && (
                <div style={{ padding: "8px 18px", color: "var(--warn-600)", fontSize: 13 }}>
                  Could not load the report - check the dates or try again.
                </div>
              )}
              {totals && (
                <div className="cc-billing-tiles" style={{ padding: "6px 18px 2px" }}>
                  <ReportTile
                    label="Collected"
                    cents={totals.collectedCents}
                    prevCents={prevTotals?.collectedCents ?? null}
                  />
                  <ReportTile
                    label="Online"
                    cents={totals.stripeCents}
                    prevCents={prevTotals?.stripeCents ?? null}
                  />
                  <ReportTile
                    label="By hand"
                    cents={totals.manualCents}
                    prevCents={prevTotals?.manualCents ?? null}
                  />
                  <ReportTile
                    label="Net"
                    cents={totals.netCents}
                    prevCents={prevTotals?.netCents ?? null}
                  />
                </div>
              )}
              {curQ.data && reportRows.length === 0 && (
                <div style={{ padding: "8px 18px 14px", color: "var(--muted)", fontSize: 13 }}>
                  No money moved in this period.
                </div>
              )}
              {totals && reportRows.length > 0 && (
                <>
                  {chartRows && (
                    <div style={{ padding: "12px 18px 4px" }}>
                      <CollectedBars rows={chartRows} />
                    </div>
                  )}
                  <div className="cc-money-scroll">
                    <div className={`cc-money-head ${REPORT_COLS}`}>
                      <span>{dayMode ? "Day" : "Month"}</span>
                      <span className="amt">Online</span>
                      <span className="amt">By hand</span>
                      <span className="amt">Collected</span>
                      <span className="amt">Returned</span>
                      <span className="amt">Net</span>
                    </div>
                    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                      {reportRows.map((m) => (
                        <li key={m.period} className={`cc-money-row ${REPORT_COLS}`}>
                          <span className="tnum" style={{ fontSize: 13 }}>
                            {periodLabel(m.period)}
                          </span>
                          <span className="amt">{fmtCents(m.stripeCents)}</span>
                          <span className="amt">{fmtCents(m.manualCents)}</span>
                          <span className="amt">{fmtCents(m.collectedCents)}</span>
                          <span className="amt">
                            {m.returnedCents > 0 ? `-${fmtCents(m.returnedCents)}` : fmtCents(0)}
                          </span>
                          <span className="amt">{fmtCents(m.netCents)}</span>
                        </li>
                      ))}
                      <li className={`cc-money-row ${REPORT_COLS}`} style={{ fontWeight: 600 }}>
                        <span style={{ fontSize: 13 }}>Total</span>
                        <span className="amt">{fmtCents(totals.stripeCents)}</span>
                        <span className="amt">{fmtCents(totals.manualCents)}</span>
                        <span className="amt">{fmtCents(totals.collectedCents)}</span>
                        <span className="amt">
                          {totals.returnedCents > 0
                            ? `-${fmtCents(totals.returnedCents)}`
                            : fmtCents(0)}
                        </span>
                        <span className="amt">{fmtCents(totals.netCents)}</span>
                      </li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="cc-detail-footer">
          <span>
            Master-file numbers come from the Excel sync{SEP}online payments settle through Stripe
            {SEP}offline ones are recorded by the office
          </span>
        </div>
      </div>
    </main>
  );
}
